import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star, Shield, Award, Phone } from 'lucide-react';
import heroImage from '@/assets/hero-deck.jpg';
import { Link } from 'react-router-dom';

export const Hero = () => {
  return (
    <section className="relative min-h-[88vh] lg:min-h-[82vh] flex items-center overflow-hidden bg-navy">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="absolute inset-0 bg-gradient-hero" />
      </div>

      {/* Content — centered */}
      <div className="relative z-10 container mx-auto px-4 pt-28 pb-16 w-full">
        <div className="max-w-4xl mx-auto text-center">

          {/* Eyebrow — Powered by All Complete Construction */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="flex items-center justify-center gap-3 mb-5"
          >
            <span className="inline-block w-10 h-[3px] bg-lime" />
            <span className="text-lime font-display font-bold uppercase tracking-[0.24em] text-[11px]">
              Powered by All Complete Construction
            </span>
            <span className="inline-block w-10 h-[3px] bg-lime" />
          </motion.div>

          {/* Headline — big, bold, slab */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.06 }}
            className="font-display text-[3rem] md:text-[4.5rem] lg:text-[5.5rem] font-bold text-white mb-4 leading-[0.95] uppercase"
          >
            Built in{' '}
            <span className="text-patriot">America.</span><br/>
            <span className="text-cream">Built to Last.</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.18 }}
            className="text-base md:text-lg text-white/75 mb-7 leading-relaxed max-w-2xl mx-auto font-body"
          >
            Family-owned deck builders with 25+ years crafting custom outdoor spaces across New Jersey. Licensed, bonded, fully insured — and proudly American.
          </motion.p>

          {/* Trust bar */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.24 }}
            className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-7 text-white/85"
          >
            <div className="flex items-center gap-1.5">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-amber text-amber" />
                ))}
              </div>
              <span className="text-sm font-semibold font-display tracking-wide">4.9/5 · 500+ Reviews</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm font-semibold font-display tracking-wide">
              <Shield className="w-4 h-4 text-patriot" />
              Licensed & Insured
            </div>
            <div className="flex items-center gap-1.5 text-sm font-semibold font-display tracking-wide">
              <Award className="w-4 h-4 text-patriot" />
              A+ BBB Rated
            </div>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-3"
          >
            <Link to="/contact">
              <Button variant="cta" size="xl" className="rounded-none font-display uppercase tracking-[0.15em] text-sm h-14 px-7 border-2 border-patriot hover:bg-patriot-dark shadow-hard hover:shadow-none hover:translate-x-1 hover:translate-y-1 transition-all">
                Get Free Estimate
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
            <a href="tel:2016570855">
              <Button variant="outline" size="xl" className="rounded-none font-display uppercase tracking-[0.15em] text-sm h-14 px-7 border-2 border-cream text-cream bg-transparent hover:bg-cream hover:text-navy">
                <Phone className="w-4 h-4" />
                (201) 657-0855
              </Button>
            </a>
          </motion.div>

          {/* Hours strip */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.42 }}
            className="text-white/50 text-xs mt-6 font-display uppercase tracking-[0.15em]"
          >
            Mon–Fri 7am–6pm · Sat 8am–4pm · Free On-Site Quote
          </motion.p>
        </div>
      </div>

      {/* Bottom flag stripe */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-patriot via-cream to-navy" />
    </section>
  );
};
